import { useState } from 'react';
import type { SetupInputsNeeded, SetupInputsAnswers } from '../types';

interface GMBSetupScreenProps {
  needed: SetupInputsNeeded;
  darkMode: boolean;
  onAutoSave: (partial: SetupInputsAnswers) => void;
  onComplete: (answers: SetupInputsAnswers) => void;
  isSubmitting: boolean;
  submissionError?: string | null;
}

const RADIUS_OPTIONS = [3, 5, 10];

function TierBadge({ label, darkMode, color }: { label: string; darkMode: boolean; color: 'red' | 'amber' | 'gray' }) {
  const classes = {
    red: darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700',
    amber: darkMode ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700',
    gray: darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${classes[color]}`}>
      {label}
    </span>
  );
}

export default function GMBSetupScreen({ needed, darkMode, onAutoSave, onComplete, isSubmitting, submissionError }: GMBSetupScreenProps) {
  const [businessName, setBusinessName] = useState(
    needed.current_business_name || needed.expected_business_name || ''
  );
  const [businessDescription, setBusinessDescription] = useState(
    needed.current_business_description || needed.expected_business_description || ''
  );
  const [confirmedCategory, setConfirmedCategory] = useState(needed.current_category || needed.expected_category || '');
  const [serviceAreasText, setServiceAreasText] = useState(
    (needed.current_values.service_areas || []).join(', ')
  );
  const [whatsappEnabled, setWhatsappEnabled] = useState(needed.current_values.whatsapp_enabled ?? false);
  const [messagingEnabled, setMessagingEnabled] = useState(needed.current_values.messaging_enabled ?? false);
  const [radius, setRadius] = useState<number | null>(needed.current_values.target_service_radius ?? null);
  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set(needed.current_values.priority_services || needed.suggested_priority_services)
  );
  const [additionalServicesText, setAdditionalServicesText] = useState('');
  // Suggestions plus anything saved earlier (including custom entries), so a
  // returning user sees and can untick every service they chose before.
  const serviceOptions = [
    ...new Set([...needed.suggested_priority_services, ...(needed.current_values.priority_services || [])]),
  ];
  const toggleService = (name: string) => {
    const next = new Set(selectedServices);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelectedServices(next);
    const additionalServices = additionalServicesText
      .split(',')
      .map((service) => service.trim())
      .filter(Boolean);
    autoSave({
      priority_services: [...new Set([...next, ...additionalServices])],
    });
  };

  // Resumability: partial saves as the user fills in Required/Conditional
  // fields, so closing the browser mid-screen doesn't lose progress —
  // does NOT set mandatory_setup_completed, so the screen still re-shows
  // (with whatever was already saved pre-filled) until final submit.
  const autoSave = (partial: SetupInputsAnswers) => onAutoSave(partial);

  const serviceAreas = serviceAreasText.split(',').map((a) => a.trim()).filter(Boolean);
  const categoryOk = !needed.category_confirmation_needed || !!confirmedCategory;
  const serviceAreasOk = !needed.service_areas_needed || serviceAreas.length > 0;
  const additionalServices = additionalServicesText.split(',').map((name) => name.trim()).filter(Boolean);
  const canComplete = businessName.trim() !== '' && categoryOk && serviceAreasOk;

  const handleComplete = () => {
    if (!canComplete) return;
    const answers: SetupInputsAnswers = {
      business_name: businessName.trim(),
      whatsapp_enabled: whatsappEnabled,
      messaging_enabled: messagingEnabled,
      target_service_radius: radius,
      priority_services: [...new Set([...selectedServices, ...additionalServices])],
      mandatory_setup_completed: true,
    };
    if (businessDescription.trim()) {
      answers.business_description = businessDescription.trim();
    }
    if (needed.category_confirmation_needed) {
      answers.confirmed_category = confirmedCategory;
    }
    if (needed.service_areas_needed) {
      answers.service_areas = serviceAreas;
    }
    onComplete(answers);
  };

  const cardClass = darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-300' : 'text-gray-600';
  const inputClass = `w-full rounded-lg px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;
  const sectionClass = `rounded-xl p-5 space-y-4 ${cardClass}`;

  return (
    <div className="flex items-start justify-center min-h-[500px] py-6">
      <div className="w-full max-w-2xl space-y-5">
        <div>
          <h2 className={`text-xl font-semibold ${textPrimary}`}>Complete your GMB setup</h2>
          <p className={`text-sm mt-1 ${textSecondary}`}>
            A few details to confirm before your dashboard is ready. Required and applicable fields must be
            completed — optional fields can be filled in later.
          </p>
        </div>

        {/* Required */}
        <div className={sectionClass}>
          <div className="flex items-center gap-2">
            <TierBadge label="Required" darkMode={darkMode} color="red" />
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Business details</h3>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1.5 ${textPrimary}`}>Business name</label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              onBlur={() => businessName.trim() && autoSave({ business_name: businessName.trim() })}
              className={inputClass}
              placeholder="Your business name"
            />
            {needed.expected_business_name && needed.current_business_name && needed.expected_business_name !== needed.current_business_name && (
              <p className={`text-xs mt-1 ${textSecondary}`}>
                Google Business Profile shows "{needed.current_business_name}" — your onboarding answer was
                "{needed.expected_business_name}". Edit above to confirm which is correct.
              </p>
            )}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1.5 ${textPrimary}`}>
              Business description <span className="text-xs font-normal opacity-70">(optional)</span>
            </label>
            <textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              onBlur={() => businessDescription.trim() && autoSave({ business_description: businessDescription.trim() })}
              rows={4}
              className={`${inputClass} resize-none`}
              placeholder="Describe your business, services, and location..."
            />
          </div>
        </div>

        {/* Conditional */}
        {(needed.category_confirmation_needed || needed.service_areas_needed || needed.whatsapp_question_required || needed.messaging_question_required) && (
          <div className={sectionClass}>
            <div className="flex items-center gap-2">
              <TierBadge label="Conditional" darkMode={darkMode} color="amber" />
              <h3 className={`text-sm font-semibold ${textPrimary}`}>Based on your profile</h3>
            </div>

            {needed.category_confirmation_needed && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${textPrimary}`}>
                  We found a possible category mismatch — which is correct?
                </label>
                <div className="space-y-2">
                  {needed.current_category && (
                    <label className={`flex items-center gap-2 text-sm ${textSecondary}`}>
                      <input
                        type="radio"
                        name="category"
                        checked={confirmedCategory === needed.current_category}
                        onChange={() => {
                          setConfirmedCategory(needed.current_category!);
                          autoSave({ confirmed_category: needed.current_category! });
                        }}
                      />
                      {needed.current_category} <span className="text-xs opacity-70">(from Google Business Profile)</span>
                    </label>
                  )}
                  {needed.expected_category && needed.expected_category !== needed.current_category && (
                    <label className={`flex items-center gap-2 text-sm ${textSecondary}`}>
                      <input
                        type="radio"
                        name="category"
                        checked={confirmedCategory === needed.expected_category}
                        onChange={() => {
                          setConfirmedCategory(needed.expected_category!);
                          autoSave({ confirmed_category: needed.expected_category! });
                        }}
                      />
                      {needed.expected_category} <span className="text-xs opacity-70">(from your onboarding answers)</span>
                    </label>
                  )}
                </div>
                {!categoryOk && <p className="text-xs text-red-500 mt-1">Pick one to continue.</p>}
              </div>
            )}

            {needed.service_areas_needed && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${textPrimary}`}>
                  What areas does your business serve?
                </label>
                <input
                  type="text"
                  placeholder="e.g. Kolkata, Howrah, Salt Lake"
                  value={serviceAreasText}
                  onChange={(e) => setServiceAreasText(e.target.value)}
                  onBlur={() => serviceAreas.length > 0 && autoSave({ service_areas: serviceAreas })}
                  className={inputClass}
                />
                <p className={`text-xs mt-1 opacity-70 ${textSecondary}`}>Comma-separated.</p>
                {!serviceAreasOk && <p className="text-xs text-red-500 mt-1">Enter at least one service area to continue.</p>}
              </div>
            )}

            {(needed.whatsapp_question_required || needed.messaging_question_required) && (
              <p className={`text-xs opacity-70 ${textSecondary}`}>
                Google doesn't share whether WhatsApp or chat is enabled on your profile, so please confirm below.
              </p>
            )}

            {needed.whatsapp_question_required && (
              <div>
                <label className={`flex items-center justify-between text-sm font-medium ${textPrimary}`}>
                  Do you use WhatsApp for customer inquiries?
                  <button
                    type="button"
                    onClick={() => {
                      const next = !whatsappEnabled;
                      setWhatsappEnabled(next);
                      autoSave({ whatsapp_enabled: next });
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      whatsappEnabled ? 'bg-teal-600' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        whatsappEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
            )}

            {needed.messaging_question_required && (
              <div>
                <label className={`flex items-center justify-between text-sm font-medium ${textPrimary}`}>
                  Do you accept messages through Google Business Profile?
                  <button
                    type="button"
                    onClick={() => {
                      const next = !messagingEnabled;
                      setMessagingEnabled(next);
                      autoSave({ messaging_enabled: next });
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      messagingEnabled ? 'bg-teal-600' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        messagingEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
            )}

          </div>
        )}

        {/* Optional */}
        <div className={sectionClass}>
          <div className="flex items-center gap-2">
            <TierBadge label="Optional" darkMode={darkMode} color="gray" />
            <h3 className={`text-sm font-semibold ${textPrimary}`}>Fine-tuning</h3>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${textPrimary}`}>Target service radius</label>
            <div className="flex gap-2">
              {RADIUS_OPTIONS.map((km) => (
                <button
                  key={km}
                  type="button"
                  onClick={() => {
                    const next = radius === km ? null : km;
                    setRadius(next);
                    autoSave({ target_service_radius: next });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    radius === km
                      ? 'bg-teal-600 border-teal-600 text-white'
                      : darkMode
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {km} km
                </button>
              ))}
            </div>
          </div>

          <div>
            {serviceOptions.length > 0 && (
              <>
              <label className={`block text-sm font-medium mb-2 ${textPrimary}`}>
                Priority services/products <span className="text-xs font-normal opacity-70">(for content & keyword alignment)</span>
              </label>
              <div className="space-y-1.5">
                {serviceOptions.map((name) => (
                  <label key={name} className={`flex items-center gap-2 text-sm ${textSecondary}`}>
                    <input
                      type="checkbox"
                      checked={selectedServices.has(name)}
                      onChange={() => toggleService(name)}
                    />
                    {name}
                  </label>
                ))}
              </div>
              </>
            )}
            <label className={`block text-sm font-medium mt-3 mb-1.5 ${textPrimary}`}>
              Additional priority services/products <span className="text-xs font-normal opacity-70">(optional, comma-separated)</span>
            </label>
            <input
              type="text"
              value={additionalServicesText}
              onChange={(event) => setAdditionalServicesText(event.target.value)}
              onBlur={() => {
                const priorityServices = [...new Set([...selectedServices, ...additionalServices])];
                autoSave({ priority_services: priorityServices });
              }}
              className={inputClass}
              placeholder="e.g. drain cleaning, emergency plumbing"
            />
          </div>
        </div>

        {submissionError && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {submissionError}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleComplete}
            disabled={isSubmitting || !canComplete}
            title={!canComplete ? 'Complete the Required and applicable Conditional fields above first' : undefined}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Complete Setup'}
          </button>
        </div>
      </div>
    </div>
  );
}
